import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { FaFacebook, FaInstagram, FaLinkedin, FaYoutube, FaTiktok } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";
import { MdLinkOff } from "react-icons/md";
import { getApiErrorMessage } from "api";
import Loader from "components/loader/Loader";
import { EmptyState, ErrorState } from "components/states";
import VisitorForm from "./components/VisitorForm";
import PrayerForm from "./components/PrayerForm";
import DonationForm from "./components/DonationForm";
import { useGoogleFonts } from "./fonts";
import { THEME_DEFAULTS, getContrastText, getLinkHubTheme } from "./theme";

const BASE_URL = process.env.REACT_APP_BASE_URL || "http://localhost:8080";

const SOCIAL_PLATFORMS = {
  facebook: { icon: FaFacebook, label: "Facebook" },
  twitter: { icon: FaXTwitter, label: "X" },
  instagram: { icon: FaInstagram, label: "Instagram" },
  linkedin: { icon: FaLinkedin, label: "LinkedIn" },
  youtube: { icon: FaYoutube, label: "YouTube" },
  tiktok: { icon: FaTiktok, label: "TikTok" },
};

const HUB_BUTTON_CLASS =
  "block w-full p-4 text-center shadow-md transition hover:scale-105 hover:shadow-lg focus-ring";

// Generate or retrieve session ID for analytics
const getSessionId = () => {
  let sessionId = sessionStorage.getItem("connect_session_id");
  if (!sessionId) {
    sessionId = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    sessionStorage.setItem("connect_session_id", sessionId);
  }
  return sessionId;
};

const PublicLinkHub = () => {
  const { slug } = useParams();
  const [profile, setProfile] = useState(null);
  const [links, setLinks] = useState([]);
  const [socialLinks, setSocialLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const pageViewTracked = useRef(false);

  const [visitorFormOpen, setVisitorFormOpen] = useState(false);
  const [prayerFormOpen, setPrayerFormOpen] = useState(false);
  const [donationFormOpen, setDonationFormOpen] = useState(false);

  useGoogleFonts([profile?.headingFont, profile?.bodyFont]);

  const trackEvent = useCallback(async (eventType, linkId = null) => {
    try {
      await axios.post(`${BASE_URL}/api/connect/public/${slug}/track`, {
        eventType,
        linkId,
        sessionId: getSessionId(),
        referrer: document.referrer || null,
      });
    } catch (e) {
      // Analytics failures must not affect visitors.
      console.debug("Analytics tracking failed:", e);
    }
  }, [slug]);

  const fetchLinkHub = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [profileRes, linksRes, socialRes] = await Promise.all([
        axios.get(`${BASE_URL}/api/connect/public/${slug}/profile`),
        axios.get(`${BASE_URL}/api/connect/public/${slug}/links`),
        axios.get(`${BASE_URL}/api/connect/public/${slug}/social-links`),
      ]);

      if (profileRes.data.success) {
        setProfile(profileRes.data.data);
      }

      if (linksRes.data.success) {
        setLinks(linksRes.data.data || []);
      }

      if (socialRes.data.success) {
        setSocialLinks(socialRes.data.data || []);
      }

      // Track page view (only once per session)
      if (!pageViewTracked.current) {
        pageViewTracked.current = true;
        trackEvent("page_view");
      }
    } catch (err) {
      console.error("Error fetching Link Hub:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [slug, trackEvent]);

  useEffect(() => {
    fetchLinkHub();
  }, [fetchLinkHub]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas">
        <Loader label="Loading" />
      </div>
    );
  }

  if (error && error.response?.status !== 404) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas text-fg">
        <ErrorState
          title="Couldn't Load This Page"
          titleAs="h1"
          message={getApiErrorMessage(error, "Check your connection and try again.")}
          onRetry={fetchLinkHub}
        />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas text-fg">
        <EmptyState
          icon={MdLinkOff}
          title="Link Hub Not Found"
          titleAs="h1"
          description="The Link Hub you're looking for doesn't exist or has been removed."
        />
      </div>
    );
  }

  const theme = getLinkHubTheme(profile);

  return (
    <div className="min-h-screen px-4 py-12" style={{ backgroundColor: theme.background }}>
      <div className="mx-auto max-w-2xl">
        <div className="mb-8 text-center">
          {profile.logoUrl && (
            <div className="mb-6 flex justify-center">
              <img
                src={profile.logoUrl}
                alt={profile.displayName}
                className="h-24 w-24 rounded-full object-cover shadow-lg"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            </div>
          )}

          <h1
            className="mb-2 text-4xl font-bold"
            style={{ color: theme.heading, fontFamily: theme.headingFont }}
          >
            {profile.displayName}
          </h1>

          {profile.tagline && (
            <p className="text-lg" style={{ color: theme.body, fontFamily: theme.bodyFont }}>
              {profile.tagline}
            </p>
          )}
        </div>

        {/* Buttons and links render in the saved buttonOrder */}
        <div className="mb-6 space-y-3">
          {(profile.buttonOrder || ["visitor", "prayer", "giving"]).map((buttonId) => {
            const settings = (profile.buttonSettings || {})[buttonId] || {};
            if (settings.visible === false) return null;

            // Managed link
            if (buttonId.startsWith("link_")) {
              const linkId = buttonId.replace("link_", "");
              const link = links.find((l) => String(l.id) === linkId);
              if (!link) return null;
              const bgColor = settings.color || THEME_DEFAULTS.button;
              return (
                <a
                  key={buttonId}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent("link_click", link.id)}
                  className={`${HUB_BUTTON_CLASS} font-medium`}
                  style={{
                    backgroundColor: bgColor,
                    color: getContrastText(bgColor),
                    borderRadius: theme.radius,
                    fontFamily: theme.bodyFont,
                  }}
                >
                  {link.title}
                </a>
              );
            }

            // Built-in action buttons
            const btnBg =
              settings.color || (buttonId === "giving" ? theme.accent : THEME_DEFAULTS.button);
            const btnStyle = {
              backgroundColor: btnBg,
              color: getContrastText(btnBg),
              borderRadius: theme.radius,
              fontFamily: theme.bodyFont,
            };

            if (buttonId === "visitor") {
              return (
                <button
                  key="visitor"
                  type="button"
                  onClick={() => { trackEvent("visitor_form_open"); setVisitorFormOpen(true); }}
                  className={`${HUB_BUTTON_CLASS} font-medium`}
                  style={btnStyle}
                >
                  {settings.text || "I'm New Here"}
                </button>
              );
            }
            if (buttonId === "prayer") {
              return (
                <button
                  key="prayer"
                  type="button"
                  onClick={() => { trackEvent("prayer_form_open"); setPrayerFormOpen(true); }}
                  className={`${HUB_BUTTON_CLASS} font-medium`}
                  style={btnStyle}
                >
                  {settings.text || "Submit Prayer Request"}
                </button>
              );
            }
            if (buttonId === "giving") {
              const label = settings.text || profile.givingButtonText || "Give";
              if (settings.url) {
                return (
                  <a
                    key="giving"
                    href={settings.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackEvent("giving_link_click")}
                    className={`${HUB_BUTTON_CLASS} font-semibold`}
                    style={btnStyle}
                  >
                    {label}
                  </a>
                );
              }
              return (
                <button
                  key="giving"
                  type="button"
                  onClick={() => { trackEvent("donation_form_open"); setDonationFormOpen(true); }}
                  className={`${HUB_BUTTON_CLASS} font-semibold`}
                  style={btnStyle}
                >
                  {label}
                </button>
              );
            }
            return null;
          })}
        </div>

        {socialLinks.length > 0 && (
          <div className="flex justify-center gap-4">
            {socialLinks.map((social) => {
              const platform = SOCIAL_PLATFORMS[social.platform];
              if (!platform) return null;
              const Icon = platform.icon;

              return (
                <a
                  key={social.id}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={platform.label}
                  className="rounded-full bg-surface p-3 text-fg shadow-md transition hover:scale-110 hover:shadow-lg focus-ring"
                >
                  <Icon aria-hidden="true" className="h-6 w-6" />
                </a>
              );
            })}
          </div>
        )}

        <div className="mt-12 text-center">
          <p className="text-sm text-fg-muted">
            Powered by{" "}
            <a
              href="https://atriumheartbeat.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-sm font-semibold hover:underline focus-ring"
            >
              Atrium
            </a>
          </p>
        </div>
      </div>

      <VisitorForm
        isOpen={visitorFormOpen}
        onClose={() => setVisitorFormOpen(false)}
        slug={slug}
        profile={profile}
        onTrackEvent={trackEvent}
      />
      <PrayerForm
        isOpen={prayerFormOpen}
        onClose={() => setPrayerFormOpen(false)}
        slug={slug}
        profile={profile}
        onTrackEvent={trackEvent}
      />
      <DonationForm
        isOpen={donationFormOpen}
        onClose={() => setDonationFormOpen(false)}
        slug={slug}
        profile={profile}
        onTrackEvent={trackEvent}
      />
    </div>
  );
};

export default PublicLinkHub;
